import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, FileText, Loader2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@akuna/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { downloadXmlImportFile, useDeleteXmlImport, useXmlImports, XmlImport } from "@/hooks/useXmlImport";

const XML_IMPORTS_PAGE_SIZE = 50;

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

export function FiscalXmlImportsTab() {
  const { t } = useTranslation("erp");
  const { data: imports = [], isLoading, isError } = useXmlImports();
  const deleteXmlImport = useDeleteXmlImport();
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const totalPages = Math.max(1, Math.ceil(imports.length / XML_IMPORTS_PAGE_SIZE));
  const paginatedImports = useMemo(() => {
    const start = (page - 1) * XML_IMPORTS_PAGE_SIZE;
    return imports.slice(start, start + XML_IMPORTS_PAGE_SIZE);
  }, [imports, page]);
  const paginatedIds = useMemo(() => paginatedImports.map((xmlImport) => xmlImport.id), [paginatedImports]);
  const selectedImports = useMemo(
    () => imports.filter((xmlImport) => selectedIds.includes(xmlImport.id)),
    [imports, selectedIds],
  );
  const allPageSelected = paginatedIds.length > 0 && paginatedIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => imports.some((xmlImport) => xmlImport.id === id)));
  }, [imports]);

  const handleDownload = async (xmlImport: XmlImport) => {
    try {
      await downloadXmlImportFile(xmlImport);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("taxes.xmlImports.downloadUnknownError");
      toast.error(t("taxes.xmlImports.downloadError", { message }));
    }
  };

  const togglePageSelection = () => {
    setSelectedIds((current) => {
      if (allPageSelected) return current.filter((id) => !paginatedIds.includes(id));
      return Array.from(new Set([...current, ...paginatedIds]));
    });
  };

  const toggleImportSelection = (id: string) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]);
  };

  const handleExportSelected = async () => {
    try {
      for (const xmlImport of selectedImports) {
        await downloadXmlImportFile(xmlImport);
      }
      toast.success(t("taxes.xmlImports.exportSuccess", { count: selectedImports.length }));
    } catch (error) {
      const message = error instanceof Error ? error.message : t("taxes.xmlImports.downloadUnknownError");
      toast.error(t("taxes.xmlImports.downloadError", { message }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{t("taxes.xmlImports.title")}</CardTitle>
            <CardDescription>{t("taxes.xmlImports.description")}</CardDescription>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            disabled={selectedImports.length === 0}
            onClick={() => void handleExportSelected()}
          >
            <Download className="h-4 w-4" />
            {t("taxes.xmlImports.exportSelected", { count: selectedImports.length })}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("taxes.xmlImports.loading")}
          </div>
        ) : null}

        {isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {t("taxes.xmlImports.loadError")}
          </div>
        ) : null}

        {!isLoading && !isError && imports.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-background text-muted-foreground">
              <FileText className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-foreground">{t("taxes.xmlImports.emptyTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("taxes.xmlImports.emptyDescription")}</p>
          </div>
        ) : null}

        {!isLoading && !isError && imports.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label={t("taxes.xmlImports.selectPage")}
                      checked={allPageSelected}
                      onCheckedChange={togglePageSelection}
                    />
                  </TableHead>
                  <TableHead>{t("taxes.xmlImports.columns.supplier")}</TableHead>
                  <TableHead>{t("taxes.xmlImports.columns.cnpj")}</TableHead>
                  <TableHead>{t("taxes.xmlImports.columns.invoice")}</TableHead>
                  <TableHead>{t("taxes.xmlImports.columns.issueDate")}</TableHead>
                  <TableHead>{t("taxes.xmlImports.columns.importedAt")}</TableHead>
                  <TableHead className="text-right">{t("taxes.xmlImports.columns.items")}</TableHead>
                  <TableHead className="text-right">{t("taxes.xmlImports.columns.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedImports.map((xmlImport) => (
                  <TableRow key={xmlImport.id}>
                    <TableCell>
                      <Checkbox
                        aria-label={t("taxes.xmlImports.selectXml", {
                          invoice: xmlImport.invoice_number || t("taxes.xmlImports.unknownInvoice"),
                        })}
                        checked={selectedIds.includes(xmlImport.id)}
                        onCheckedChange={() => toggleImportSelection(xmlImport.id)}
                      />
                    </TableCell>
                    <TableCell className="min-w-56 font-medium">{xmlImport.supplier_name || "-"}</TableCell>
                    <TableCell>{xmlImport.supplier_cnpj || "-"}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p>{xmlImport.invoice_number || "-"}</p>
                        {xmlImport.invoice_key ? (
                          <p className="max-w-48 truncate text-xs text-muted-foreground">{xmlImport.invoice_key}</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(xmlImport.invoice_issued_at)}</TableCell>
                    <TableCell>{formatDate(xmlImport.imported_at)}</TableCell>
                    <TableCell className="text-right">{xmlImport.line_count ?? 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleDownload(xmlImport)}>
                          <Download className="mr-2 h-4 w-4" />
                          {t("taxes.xmlImports.download")}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              disabled={deleteXmlImport.isPending}
                              aria-label={t("taxes.xmlImports.delete")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("taxes.xmlImports.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("taxes.xmlImports.deleteDescription", {
                                  supplier: xmlImport.supplier_name || t("taxes.xmlImports.unknownSupplier"),
                                  invoice: xmlImport.invoice_number || t("taxes.xmlImports.unknownInvoice"),
                                })}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("taxes.xmlImports.cancelDelete")}</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteXmlImport.mutate(xmlImport)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {t("taxes.xmlImports.confirmDelete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
              <span>
                {t("taxes.xmlImports.pagination", {
                  from: imports.length === 0 ? 0 : (page - 1) * XML_IMPORTS_PAGE_SIZE + 1,
                  to: Math.min(page * XML_IMPORTS_PAGE_SIZE, imports.length),
                  total: imports.length,
                })}
              </span>
              {totalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-4 w-4" />
                    {t("taxes.xmlImports.previous")}
                  </Button>
                  <span>{t("taxes.xmlImports.pageIndicator", { page, totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page === totalPages}>
                    {t("taxes.xmlImports.next")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
